import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { buildUrl, isConnectMessageType, unwrap, UtiqPass } from './utils';
import { openInIframe } from '@shared/iFrameHandler/openInIframe';
import { connect } from './idConnectConnect';
import { isTroubleshootingUrl } from '@idConnect/identification/troubleshooting/handleTroubleshootService';
import { setCookieDomain } from './idConnectSetCookieDomain';
import { handleConsentRepromptRequirement } from './handleConsentRepromptRequirement';
import { mnoSelector } from './idConnectMnoSelector';
import { getData } from './idConnectGetData';
import { idConnectStart } from './idConnectStart';
import { acceptConsent } from './actions';
import { getVersions } from '@shared/helpers/getVersions';
import { getIdGraphEntry } from '@idConnect/api/getIdGraphEntry';

export const idConnect: Maker<Ctx, Snap, null> = ({
  makeEffect,
  makeEffectGroup,
}) => [
  makeEffect(
    'initVersionCookiesForLegacyUsers',
    (_, { ctx, snap, pass, skip }) => {
      if (ctx.isIdConnect) return skip('isIdConnect');
      if (snap.cookies.utiq_consent_status !== 'true')
        return skip("consent status !== 'true'");
      if (snap.cookies.utiq_consent_version)
        return skip('version cookie already set');
      const popupContent = ctx.window.Utiq?.data?.popupContent;
      if (!popupContent?.version)
        return skip('popupContent.version not available');
      const { majorVersion } = getVersions(popupContent.version);
      if (majorVersion === null || majorVersion >= 3)
        return skip('domain version >= 3, reprompt may be needed');
      return pass(popupContent);
    },
    (popupContent, { snap, ctx }) => {
      acceptConsent(snap, ctx, popupContent);
    },
  ),

  makeEffect(
    'getIdGraphEntry.bind',
    (_, { ctx, skip, pass }) => {
      if (!ctx.window?.Utiq?.API) return skip('not inited');
      if (ctx.times.getIdGraphEntryBound) return skip('already done');
      return pass(ctx.window?.Utiq?.API);
    },

    (API, { ctx, snap }) => {
      ctx.times.getIdGraphEntryBound = snap.now;

      API.getIdGraphEntry = (entryKey?: string) => {
        return getIdGraphEntry(entryKey!, ctx.window.IDCONNECT_CONFIG!) as any;
      };
    },
  ),

  makeEffectGroup(
    'handleConsentRepromptRequirement',
    (_, { pass }) => pass(null),
    handleConsentRepromptRequirement,
  ),

  makeEffect(
    'utiqPassInitJournal',
    (_, { ctx, cond }) =>
      cond(
        ctx.journal.utiqPass === undefined,
        'ctx.journal.utiqPass === undefined',
      ),
    (_, { ctx, snap }) => {
      ctx.journal.utiqPass = JSON.stringify(snap.localStorage.utiqPass);
    },
  ),

  makeEffectGroup(
    'idConnectStart',
    (_, { pass }) => pass(null),
    idConnectStart,
  ),

  makeEffectGroup(
    'msg',
    (_, { ctx, skip, pass }) => {
      const inPostBox = ctx.iframeMsgs?.[0];
      if (!inPostBox) return skip('empty postbox');

      const { msg } = inPostBox;
      if (!isConnectMessageType(msg)) return skip('message invalid');

      return pass(msg);
    },
    ({ makeEffectGroup }) => [
      makeEffectGroup(
        'GETDATA',
        (msg, { pass, skip }) =>
          msg.msgType === 'GETDATA'
            ? pass(msg, 'msgType', msg.msgType)
            : skip('msgType', msg.msgType),
        getData,
      ),

      makeEffectGroup(
        'CONNECT',
        (msg, { pass, skip }) =>
          msg.msgType === 'CONNECT'
            ? pass(msg, 'msgType', msg.msgType)
            : skip('msgType', msg.msgType),
        connect,
      ),

      makeEffectGroup(
        'MNOSELECTOR',
        (msg, { pass, skip }) =>
          msg.msgType === 'MNOSELECTOR'
            ? pass(msg, 'msgType', msg.msgType)
            : skip('msgType', msg.msgType),
        mnoSelector,
      ),

      makeEffectGroup(
        'SETCOOKIEDOMAIN',
        (msg, { pass, skip }) =>
          msg.msgType === 'SETCOOKIEDOMAIN'
            ? pass(msg, 'msgType', msg.msgType)
            : skip('msgType', msg.msgType),
        setCookieDomain,
      ),
    ],
  ),

  makeEffect(
    '/op/idconnect/token-stub',
    (_, { ctx, skip, pass }) => {
      const { resolvingConnectId, idConnectMnoSelectorDomain } = ctx.state;
      if (!resolvingConnectId) return skip('!resolvingConnectId');
      if (!idConnectMnoSelectorDomain)
        return skip('!idConnectMnoSelectorDomain');

      if (!ctx.axios.active[0]?.res) return skip('!ctx.axios.active[0]?.res');
      const { req, res: res } = unwrap(ctx.axios.active[0]);

      if (!req.url?.includes('/op/idconnect/token-stub'))
        return skip("!req.url?.includes('/op/idconnect/token-stub')");

      const token = res?.data?.token;

      if (!token) return skip('!token');

      const url = buildUrl(ctx, ['idConnectConfig'])
        ?.tryPathFrom(
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            .IDCONNECT_API_HOST,
        )
        ?.addPathSegment('tmit-stub')
        .withParam('token', token)
        .withParam('diagnostic', isTroubleshootingUrl() ? 'true' : undefined)
        .withParam(
          'd',
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            .IDCONNECT_API_HOST
            ? new URL(
                ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG.IDCONNECT_API_HOST,
              ).hostname
            : undefined,
        )
        .render();

      if (!url) return skip('url unbuildable');

      return pass(url);
    },
    (url, { ctx }) => {
      ctx.axios.active.shift();

      openInIframe({
        iFrameUrl: url,
        iFrameId: 'UTIQSTUB',
        environment: 'Utiq',
      });
    },
  ),

  makeEffectGroup(
    'utiqPassJournalChanged',
    (_, { ctx, cond, snap }) =>
      cond(
        ctx.journal.utiqPass !== JSON.stringify(snap.localStorage.utiqPass),
        'ctx.journal.utiqPass === undefined',
      ),

    ({ makeEffect }) => [
      makeEffect(
        'ids_set',
        (_, { ctx, skip, pass, snap }) => {
          if (typeof ctx.journal.utiqPass !== 'string')
            return skip('journal.utiqPass corrupt');

          const oldPass = JSON.parse(ctx.journal.utiqPass) as UtiqPass | null;
          const newPass = snap.localStorage.utiqPass;

          if (newPass === null) return skip('newPass === null');

          if (
            typeof oldPass?.connectId?.value !== 'string' &&
            typeof newPass?.connectId?.value === 'string'
          )
            return pass({
              oldMtid: oldPass?.connectId?.value,
              newMtid: newPass?.connectId?.value,
            });

          return skip('');
        },
        (_, { ctx, snap }) => {
          ctx.journal.utiqPass = JSON.stringify(snap.localStorage.utiqPass);
          ctx.analytics['ids_set'] = snap.now;
        },
      ),

      makeEffect(
        'ids_updated',
        (_, { ctx, skip, pass, snap, log }) => {
          if (typeof ctx.journal.utiqPass !== 'string')
            return skip('journal.utiqPass corrupt');

          const oldPass = JSON.parse(ctx.journal.utiqPass);
          const newPass = snap.localStorage.utiqPass;

          if (oldPass === null) return skip('oldPass === null');
          if (newPass === null) return skip('newPass === null');

          log(newPass.connectId);
          if (
            typeof oldPass.connectId?.idGraph?.[0]?.mtid === 'string' &&
            oldPass.connectId?.idGraph?.[0]?.mtid !==
              newPass.connectId?.idGraph?.[0]?.mtid
          )
            return pass({
              oldMtid: oldPass?.connectId?.idGraph?.[0]?.mtid,
              newMtid: newPass?.connectId?.idGraph?.[0]?.mtid,
            });

          return skip('');
        },
        (_, { ctx, snap }) => {
          ctx.journal.utiqPass = JSON.stringify(snap.localStorage.utiqPass);
          ctx.analytics['ids_updated'] = snap.now;
        },
      ),

      makeEffect(
        'default',
        (_, { pass }) => pass(''),
        (_, { ctx, snap }) => {
          ctx.journal.utiqPass = JSON.stringify(snap.localStorage.utiqPass);
        },
      ),
    ],
  ),
];
